import React, { ReactElement, useState } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";

const Container = styled(TableContainer)`
  td,
  th {
    padding: 14px;
  }

  .body-row {
    :hover {
      cursor: pointer;
    }
  }

  td.linked-cell {
    padding: 0;
  }

  .row-link {
    display: block;
    padding: 14px;
    color: inherit;
    text-decoration: none;

    :focus-visible {
      outline: 2px solid currentColor;
      outline-offset: -2px;
    }
  }
`;

const Pagination = styled(TablePagination)`
  p {
    margin: 0;
  }
`;

export interface ITableSetting<T> {
  id: string;
  align?: "left" | "right" | "center";
  name: string;
  path: string;
  content: (data: T, index: number) => ReactElement | string;
}

export interface IRowLink {
  to: string;
  state?: unknown;
}

export interface IBasicTableItem {
  getDisplayName(): string;
  getUniqueId(): string;
}

interface IBasicTable<T> {
  tableData: T[];
  tableSettings: ITableSetting<T>[];
  onRowClick?: (id: string) => void;
  getRowLink?: (row: T) => IRowLink;
  paginationControls?: {
    paginationCount: number;
    handlePagination: (page: number) => void;
    handleRowsPerPage: (pageSize: number) => void;
    currentPage: number;
    currentRowsPerPage: number;
  };
}

export const BasicTable = <T extends IBasicTableItem>(
  props: IBasicTable<T>
) => {
  const {
    tableData,
    tableSettings,
    onRowClick,
    getRowLink,
    paginationControls: {
      paginationCount,
      handlePagination,
      handleRowsPerPage,
      currentPage,
      currentRowsPerPage,
    } = {},
  } = props;

  const navigate = useNavigate();
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);

  const handleChangePage = (event, newPage) => {
    if (handlePagination) {
      handlePagination(newPage + 1);
    } else setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    if (handleRowsPerPage && handlePagination) {
      handleRowsPerPage(event.target.value);
    } else {
      setRowsPerPage(event.target.value);
      setPage(1);
    }
  };

  const handlePaginationSlicing = (tableData: T[]) => {
    //The pagination is handled by parent component or lazyloading
    if (handlePagination) {
      return tableData;
    }
    //else all the data is sent in and pagination is handled here on the client
    return tableData.slice(
      (page - 1) * rowsPerPage,
      (page - 1) * rowsPerPage + rowsPerPage
    );
  };

  return (
    <Paper sx={{ width: "100%" }}>
      <Container>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              {tableSettings.map((column, index) => (
                <TableCell key={column.id + index} align={column.align}>
                  {column.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {handlePaginationSlicing(tableData).map((row, index) => {
              const link = getRowLink?.(row);
              const handleClick = link
                ? (event: React.MouseEvent<HTMLTableRowElement>) => {
                    if ((event.target as HTMLElement).closest("a")) return;
                    navigate(link.to, { state: link.state });
                  }
                : onRowClick
                  ? () => onRowClick(row.getUniqueId())
                  : undefined;

              return (
                <TableRow
                  className="body-row"
                  hover
                  key={row.getUniqueId() + index}
                  onClick={handleClick}
                >
                  {tableSettings.map((column, columnIndex) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      className={link ? "linked-cell" : undefined}
                    >
                      {link ? (
                        <Link
                          className="row-link"
                          to={link.to}
                          state={link.state}
                          tabIndex={columnIndex === 0 ? undefined : -1}
                        >
                          {column.content(row, index)}
                        </Link>
                      ) : (
                        column.content(row, index)
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Container>
      <Pagination
        rowsPerPageOptions={[10, 25, 50]}
        {...({ component: "div" } as any)}
        count={paginationCount ?? tableData.length}
        rowsPerPage={currentRowsPerPage ?? rowsPerPage}
        page={currentPage ? currentPage - 1 : page - 1}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
};
